import { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Sparkles, Zap, Crown, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const API = `${BACKEND_URL}/api`;

const CreditPurchase = ({ open, onClose, onSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(false);

  useEffect(() => {
    if (open && packages.length === 0) {
      loadPackages();
    }
    if (!open) {
      setSelectedPackage(null);
    }
  }, [open]);

  const loadPackages = async () => {
    setPackagesLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/paypal/packages`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error("Error loading packages:", error);
      toast.error("Fehler beim Laden der Pakete");
    } finally {
      setPackagesLoading(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const createOrder = async () => {
    try {
      const response = await axios.post(
        `${API}/paypal/create-order`,
        { package_id: selectedPackage.id },
        getAuthHeaders()
      );
      return response.data.order_id;
    } catch (error) {
      console.error("Error creating order:", error);
      const msg = error.response?.data?.detail || "Fehler beim Erstellen der Bestellung";
      toast.error(msg);
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/paypal/capture-order`,
        { order_id: data.orderID },
        getAuthHeaders()
      );
      toast.success(`Zahlung erfolgreich! ${response.data.credits_added} Credits hinzugefügt!`);
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error("Error capturing order:", error);
      toast.error("Fehler beim Abschließen der Zahlung");
    } finally {
      setLoading(false);
    }
  };

  const getPackageIcon = (pkg) => {
    if (pkg.id?.startsWith('starter')) return <Sparkles className="w-6 h-6" />;
    if (pkg.id?.startsWith('professional')) return <Zap className="w-6 h-6" />;
    if (pkg.id?.startsWith('business')) return <Crown className="w-6 h-6" />;
    return <Check className="w-6 h-6" />;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="credit-purchase-dialog">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-gray-900">Credits kaufen</DialogTitle>
          <DialogDescription>
            Wählen Sie ein Monatsabo und zahlen Sie sicher mit PayPal
          </DialogDescription>
        </DialogHeader>

        {!selectedPackage ? (
          <>
            {packagesLoading ? (
              <div className="text-center py-16">
                <Loader2 className="w-10 h-10 animate-spin mx-auto text-[#2835F7] mb-3" />
                <p className="text-gray-600">Pakete werden geladen...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                {packages.map((pkg) => (
                  <Card 
                    key={pkg.id}
                    data-testid={`package-card-${pkg.id}`}
                    className={`relative cursor-pointer transition-all duration-300 hover:shadow-xl border-2 ${
                      pkg.popular ? 'border-[#2835F7] shadow-lg' : 'border-gray-200 hover:border-[#2835F7]'
                    }`}
                    onClick={() => setSelectedPackage(pkg)}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#2835F7] text-white px-4 py-1 rounded-full text-xs font-bold">
                        BELIEBT
                      </div>
                    )}
                    
                    <CardHeader className="text-center pb-4">
                      <div className={`w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center ${
                        pkg.popular ? 'bg-gradient-to-br from-[#2835F7] to-[#1a25c9]' : 'bg-gray-100'
                      }`}>
                        <div className={pkg.popular ? 'text-white' : 'text-gray-600'}>
                          {getPackageIcon(pkg)}
                        </div>
                      </div>
                      <CardTitle className="text-xl font-bold">{pkg.name}</CardTitle>
                      <CardDescription className="text-sm">{pkg.description}</CardDescription>
                    </CardHeader>

                    <CardContent className="text-center">
                      <div className="mb-4">
                        <div className="text-4xl font-bold text-gray-900">{pkg.price.toFixed(2)}€</div>
                        <div className="text-sm text-gray-600 mt-1">pro Monat</div>
                        <div className="text-xs text-gray-500 mt-1">{pkg.credits} Credits monatlich</div>
                      </div>

                      <div className="mb-4 pb-4 border-b">
                        <div className="text-xs text-gray-500">Pro Kontakt</div>
                        <div className="text-lg font-bold text-[#2835F7]">{pkg.price_per_contact.toFixed(2)}€</div>
                      </div>

                      <ul className="space-y-2 text-left text-sm">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>{pkg.credits} Kontakte/Monat</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Credits verfallen nicht</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Jederzeit kündbar</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Sichere PayPal-Zahlung</span>
                        </li>
                      </ul>

                      <Button 
                        data-testid={`select-package-${pkg.id}`}
                        className={`w-full mt-6 ${
                          pkg.popular 
                            ? 'bg-gradient-to-r from-[#2835F7] to-[#1a25c9] hover:from-[#1a25c9] hover:to-[#0d1570]' 
                            : 'bg-gray-900 hover:bg-gray-800'
                        } text-white font-bold rounded-xl`}
                        onClick={(e) => { e.stopPropagation(); setSelectedPackage(pkg); }}
                      >
                        Auswählen
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="mt-6">
            <Button 
              variant="outline" 
              onClick={() => setSelectedPackage(null)}
              className="mb-6"
              data-testid="back-to-packages-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zur Paketauswahl
            </Button>

            <Card className="border-2 border-[#2835F7]">
              <CardHeader className="bg-gradient-to-r from-[#2835F7] to-[#1a25c9] text-white">
                <CardTitle className="text-2xl">{selectedPackage.name} Monatsabo</CardTitle>
                <CardDescription className="text-white/90">
                  {selectedPackage.price.toFixed(2)}€/Monat · {selectedPackage.credits} Credits monatlich · {selectedPackage.price_per_contact.toFixed(2)}€ pro Kontakt
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-8">
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-gray-700">
                  <strong>Widerrufsbelehrung:</strong> Sie haben ein 14-tägiges Widerrufsrecht ab Vertragsschluss. Mit Klick auf "Bezahlen" stimmen Sie den AGB zu und erklären, dass die Leistung sofort beginnt.
                </div>

                {!PAYPAL_CLIENT_ID ? (
                  <div className="text-center py-6 text-red-600">PayPal ist nicht konfiguriert.</div>
                ) : (
                  <div data-testid="paypal-buttons-container">
                    <PayPalScriptProvider options={{ 
                      "client-id": PAYPAL_CLIENT_ID,
                      currency: "EUR",
                      intent: "capture"
                    }}>
                      <PayPalButtons
                        style={{ layout: "vertical", shape: "rect", label: "pay", height: 45 }}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onError={(err) => {
                          console.error("PayPal error:", err);
                          toast.error("PayPal Fehler. Bitte versuchen Sie es erneut.");
                        }}
                        disabled={loading}
                        forceReRender={[selectedPackage.id]}
                      />
                    </PayPalScriptProvider>
                  </div>
                )}

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Hinweis:</strong> Nach erfolgreicher Zahlung werden die Credits sofort gutgeschrieben.
                    Sie können das Abo jederzeit zum Monatsende kündigen.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreditPurchase;
